// Agent state types
export enum ListeningState {
  LISTENING = 1, // waiting for wake word to engage
  FOCUSED = 2, // wake word received, awaiting end of turn
  THINKING = 3, // processing turn
  SPEAKING = 4, // processing thinking response
}

/**
 * Turn mode determines how a user's speaking turn ends.
 * - PTT: Turn only ends when user explicitly clicks PTT again (walkie-talkie style)
 * - VAD: Turn ends automatically after silence timeout (voice activity detection)
 */
export enum TurnMode {
  PTT = 'ptt',
  VAD = 'vad',
}

/**
 * One utterance of a turn, as the speech service delivered it (#4278).
 *
 * A wake-word turn is a transcript of EVERYONE who spoke in the window, flattened
 * to "Alias: text" lines. A command has to be read from one person's one
 * utterance, so the turn also carries its messages unflattened.
 */
export interface AgentTurnMessage {
  messageId: string;
  userId: string;
  text: string;
  /** Numeric DirectiveType: whether THIS utterance carried the wake word. */
  directiveType?: number;
  /** The utterance with its wake phrase removed, when it carried one. */
  directiveMessage?: string;
  /** What was said before the wake phrase; "" when the name led the sentence. Absent from an older speech service. */
  directivePrefix?: string;
  speakerProvenance?: 'participant' | 'companion';
}

/** How a turn came to be addressed to the companion: a held key, or the wake word. */
export type AgentTurnAddressing = 'ptt' | 'wake';

export interface AgentState {
  enabled: boolean;
  listeningState: ListeningState;
  lastUpdated?: number;
}

// Add the moved AgentType enum here
/**
 * Defines the types of agents supported by the prompt service
 */
export enum AgentType {
  Conversational = 'conversational_agent',
  RoomContext = 'room_context_agent',
  Tele = 'tele_agent'
}

/**
 * FEAT-028: EmotionalState enum for AI Talk Show companion expressions.
 * Used by the animation system to drive companion facial/body expressions
 * and scene rendering during multi-companion conversations.
 */
export enum EmotionalState {
  NEUTRAL = 'neutral',
  EXCITED = 'excited',
  FRUSTRATED = 'frustrated',
  AMUSED = 'amused',
  SKEPTICAL = 'skeptical',
  SURPRISED = 'surprised',
  CONTEMPLATIVE = 'contemplative',
  HEATED = 'heated',
  AGREEING = 'agreeing',
  /**
   * Added 2026-08-25 from evidence, not anticipation.
   *
   * The original nine were authored for a talk show, where `heated`, `skeptical` and `agreeing`
   * are debate states. A companion in a one-to-one room reaches for a different register: asked
   * about a cancelled flight it emitted `[EMOTION concerned]` and `[EMOTION sympathetic 0.6]`,
   * both apt, and both discarded because the vocabulary had no word for them.
   *
   * `curious` was considered and NOT added — nothing has asked for it. The discard log in
   * `emotionTag.ts` is how the next word earns its place.
   */
  CONCERNED = 'concerned',
  SYMPATHETIC = 'sympathetic',
  /**
   * Added 2026-08-26, from a gap the discard log structurally cannot see.
   *
   * The operator, on a reply to "my Cocker Spaniel makes me happy": she tagged `amused`, which
   * means finding something FUNNY — amused AT you rather than happy WITH you, and it read smug.
   *
   * The evidence is different in kind from CONCERNED and SYMPATHETIC, and that difference is
   * the point. Those two were words the model VOLUNTEERED and the parser discarded, so the
   * discard log caught them. This instruction LISTS the valid words, so the model cannot reach
   * past them — it settles for the nearest instead, and settling leaves no discard to log.
   *
   * The signature of a settled gap is OVER-SELECTION: five of eight consecutive tags were
   * `amused`, in exchanges that were warm rather than funny. That is the only trace this kind
   * of gap produces, and it is now the instrument for finding the next one.
   */
  HAPPY = 'happy',
  /**
   * Added 2026-09-02, and NOT by the route `concerned` and `sympathetic` took.
   *
   * Those two earned their place through the discard log: the model emitted them, the vocabulary
   * had no word, and the discards made the case. `sad` could never arrive that way, because the
   * prompt hands the model the valid list up front — so it never emits `[EMOTION sad]`, nothing
   * is ever discarded, and the discovery loop stays blind to precisely the words already ruled
   * out. That loop finds words the vocabulary is missing; it cannot find words it has excluded.
   *
   * The evidence instead: an operator asked a companion in a room to look sad. It reached for
   * `sympathetic` — the nearest legal option and one of the subtlest expressions in the set — and
   * the request visibly went unmet. Session 2026-09-02, room 05055781.
   */
  SAD = 'sad',
}

/**
 * FEAT-029: Per-companion emotional state snapshot.
 * Sent from the signaling server to clients to drive companion
 * animation (facial expression, body language, scene effects).
 */
export interface CompanionEmotionalState {
  /** Unique companion identifier (matches TalkShowCompanionConfig.agentId) */
  companionId: string;
  /** Current emotional state */
  emotion: EmotionalState;
  /** Intensity of the emotion, from 0 (barely noticeable) to 1 (maximum) */
  intensity: number;
}